import { useCallback, useEffect, useRef, useState } from 'react'
import { useGlobalSelectedCompany } from 'src/context/GlobalSelectedCompanyContext'
import { OrderDirection, ToggleFilterType } from '../types'

interface UseTableDataProps {
  getData: (options: { params: Record<string, any> }) => Promise<{ data: any[]; size: number }>
  defaultPageSize?: number
}

const SEARCH_DEBOUNCE_MS = 350

export function useTableData({ getData, defaultPageSize = 15 }: UseTableDataProps) {
  const { selectedCompanyId, isGlobalAdminSelected } = useGlobalSelectedCompany()

  const [rows, setRows] = useState<any[]>([])
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState<number>(defaultPageSize)
  const [loading, setLoading] = useState(false)
  const [orderBy, setOrderBy] = useState<OrderDirection>('desc')
  const [termSearch, setTermSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<ToggleFilterType>(null)

  // Ref for debounce timer
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const getCompanyParams = () => isGlobalAdminSelected() ? {} : { companyId: selectedCompanyId }

  const fetchData = async (options?: { params: Record<string, any> }) => {
    setLoading(true)
    try {
      const result = await getData({
        params: { ...options?.params, orderBy },
      })
      setRows(result?.data || [])
      setTotalPages(Math.ceil((result?.size || 0) / rowsPerPage))
    } catch (error) {
      console.error('[Table] Error fetching data:', error)
      setRows([])
      setTotalPages(1)
    } finally {
      setLoading(false)
    }
  }

  // Debounced search: waits SEARCH_DEBOUNCE_MS after last keystroke before fetching
  const handleSearch = useCallback((search: string) => {
    setTermSearch(search)
    setPage(0)

    // Clear any pending debounce timer
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current)
    }

    searchTimerRef.current = setTimeout(() => {
      fetchData({ params: { search, size: rowsPerPage, ...getCompanyParams() } })
    }, SEARCH_DEBOUNCE_MS)
  }, [rowsPerPage]) // eslint-disable-line

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current)
      }
    }
  }, [])

  const handleSelectFilter = useCallback((
    _event: React.MouseEvent<HTMLElement>,
    filter: ToggleFilterType
  ) => {
    setActiveFilter(filter)
    // Data refresh is handled by the useEffect dependency on activeFilter
  }, [])

  const handlePageSizeChange = useCallback((newSize: number) => {
    setRowsPerPage(newSize)
    setPage(0)
  }, [])

  const refreshData = useCallback(() => {
    const filterParams = activeFilter !== null
      ? { [`${activeFilter}`]: true }
      : {}
    fetchData({
      params: {
        page: 0,
        size: rowsPerPage,
        ...filterParams,
        ...getCompanyParams(),
      },
    })
  }, [activeFilter, rowsPerPage]) // eslint-disable-line

  useEffect(() => {
    const filterParams = activeFilter !== null
      ? { [`${activeFilter}`]: true }
      : {}
    fetchData({
      params: {
        page,
        size: rowsPerPage,
        ...filterParams,
        ...getCompanyParams(),
      },
    })
  }, [page, rowsPerPage, orderBy, activeFilter, selectedCompanyId]) // eslint-disable-line

  return {
    rows,
    page,
    totalPages,
    rowsPerPage,
    loading,
    orderBy,
    termSearch,
    activeFilter,
    setPage,
    setRowsPerPage: handlePageSizeChange,
    setOrderBy,
    setActiveFilter,
    handleSearch,
    handleSelectFilter,
    fetchData,
    refreshData,
    getCompanyParams,
  }
}
